import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Row, Col, Card, Button, Table } from "react-bootstrap";
import Fancybox from "../../Component/FancyBox";
import Layout from "../../layout/Layout";
import FormSkeleton from "../../Component/theme-management/FormSkeleton";
import ErrorState from "../../Component/theme-management/ErrorState";
import ImageWithFallback from "../../Component/theme-management/ImageWithFallback";
import { themesApi, isAuthError } from "../../helpers/tmClient";
import { THEME_TYPE_OPTIONS, labelOf } from "../../helpers/tmConstants";

/**
 * Light-background badges (dark text, rounded) used across the theme
 * detail header — consistent with the admin panel & the Themes list.
 */
const BADGE_STYLE = {
  typeHome: { backgroundColor: "#e7f1ff", color: "#0b5ed7" },
  typeLock: { backgroundColor: "#efe7fb", color: "#6f42c1" },
  category: { backgroundColor: "#e8f5e9", color: "#1b5e20" },
  premiumOn: { backgroundColor: "#fff3cd", color: "#664d03" },
  premiumOff: { backgroundColor: "#e9ecef", color: "#495057" },
  statusOn: { backgroundColor: "#e6f4ea", color: "#0f5132" },
  statusOff: { backgroundColor: "#fbe3e4", color: "#842029" },
};

/** Read-only theme detail with its icons and widgets. */
const ThemeView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await themesApi.detail(id);
      if (isAuthError(res)) return navigate("/");
      setDetail(res.data.data);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const Img = ({ src, label }) =>
    src ? (
      <div className="me-3 text-center">
        <Fancybox>
          <a data-fancybox href={src}>
            <ImageWithFallback src={src} alt={label} className="rounded-2 bg-light" style={{ height: 90, width: 90, objectFit: "cover" }} />
          </a>
        </Fancybox>
        <div className="small text-muted mt-1">{label}</div>
      </div>
    ) : null;

  if (loading) {
    return (
      <Layout sidebar={true}>
        <div className="page-heading"><h3 className="my-1">Theme Details</h3></div>
        <div className="page-content">
          <FormSkeleton fields={3} images={4} />
        </div>
      </Layout>
    );
  }
  if (loadError || !detail) {
    return (
      <Layout sidebar={true}>
        <div className="page-heading"><h3 className="my-1">Theme Details</h3></div>
        <div className="page-content">
          <Card><Card.Body>
            <ErrorState asRow={false} message="Failed to load theme details." onRetry={load} />
          </Card.Body></Card>
        </div>
      </Layout>
    );
  }
  const { theme, icons, widgets } = detail;

  return (
    <Layout sidebar={true}>
      <div className="page-heading d-flex justify-content-between align-items-center">
        <h3 className="my-1">Theme Details</h3>
        <div>
          <Link to={`/themes/edit/${theme._id}`}><Button variant="primary" className="me-2">Edit</Button></Link>
          <Link to="/themes"><Button variant="secondary">Back</Button></Link>
        </div>
      </div>
      <div className="page-content">
        <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col md={6}>
                <h5>{theme.name}</h5>
                <p className="mb-1 text-capitalize">
                  <span
                    className="badge rounded-pill me-2"
                    style={theme.theme_type === "home" ? BADGE_STYLE.typeHome : BADGE_STYLE.typeLock}
                  >
                    {labelOf(THEME_TYPE_OPTIONS, theme.theme_type)}
                  </span>
                  <span
                    className="badge rounded-pill me-2"
                    style={theme.premium ? BADGE_STYLE.premiumOn : BADGE_STYLE.premiumOff}
                  >
                    {theme.premium ? "Premium" : "Free"}
                  </span>
                  <span
                    className="badge rounded-pill"
                    style={theme.status ? BADGE_STYLE.statusOn : BADGE_STYLE.statusOff}
                  >
                    {theme.status ? "Active" : "Inactive"}
                  </span>
                </p>
                <p className="mb-1">
                  <strong>Category:</strong>{" "}
                  {theme.category ? (
                    <span className="badge rounded-pill" style={BADGE_STYLE.category}>
                      {theme.category}
                    </span>
                  ) : (
                    "-"
                  )}
                </p>
                <p className="mb-1"><strong>Sort Order:</strong> {theme.sort_order}</p>
                <p className="mb-1">
                  <strong>Counts:</strong> {theme.icons_count || 0} icons ·{" "}
                  {theme.widgets_count || 0} widgets · {theme.wallpapers_count || 0} wallpapers
                </p>
              </Col>
              <Col md={6}>
                <div className="d-flex flex-wrap">
                  <Img src={theme.thumbnail} label="Thumbnail" />
                  <Img src={theme.thumbnail_ipad} label="Thumbnail iPad" />
                  <Img src={theme.preview_image} label="Preview" />
                  <Img src={theme.preview_image_ipad} label="Preview iPad" />
                </div>
              </Col>
            </Row>
            {theme.theme_type === "lock" && (theme.lock_screen_color || theme.color_preview_image) && (
              <>
                <hr />
                <Row>
                  <Col md={6}>
                    <p className="mb-1"><strong>Lock Screen Color:</strong></p>
                    {theme.lock_screen_color ? (
                      <div className="d-flex align-items-center">
                        <div
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: 6,
                            border: "1px solid #ccc",
                            backgroundColor: theme.lock_screen_color,
                          }}
                          className="me-2"
                        ></div>
                        <span className="text-uppercase">{theme.lock_screen_color}</span>
                      </div>
                    ) : (
                      "-"
                    )}
                  </Col>
                  <Col md={6}>
                    <p className="mb-1"><strong>Color Preview Image:</strong></p>
                    <Img src={theme.color_preview_image} label="Color Preview" />
                  </Col>
                </Row>
              </>
            )}
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <strong>Icon Sets ({icons?.length || 0})</strong>
            <Link to="/theme-icons/add"><Button size="sm" variant="outline-primary">Add Icon Set</Button></Link>
          </Card.Header>
          <Card.Body>
            <Table bordered responsive className="mb-0">
              <thead><tr><th>#</th><th>Thumbnail</th><th>Icons</th><th>Premium</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {icons && icons.length ? icons.map((set, i) => (
                  <tr key={set._id}>
                    <td>{i + 1}</td>
                    <td><ImageWithFallback src={set.thumbnail_image} alt="" className="hv-40 rounded-2" /></td>
                    <td>
                      <span className="me-2">{set.icons?.length || 0} icon(s)</span>
                      {(set.icons || []).slice(0, 6).map((ic) => (
                        ic.icon_image ? <ImageWithFallback key={ic._id} src={ic.icon_image} alt={ic.icon_name} className="hv-30 rounded-2 me-1 bg-light" /> : null
                      ))}
                      {set.icons?.length > 6 && <span className="text-muted small">+{set.icons.length - 6} more</span>}
                    </td>
                    <td>{set.premium ? "Yes" : "No"}</td>
                    <td>{set.status ? "Active" : "Inactive"}</td>
                    <td className="text-end">
                      <Link to={`/theme-icons/edit/${set._id}`}><Button size="sm" variant="outline-primary" className="btn-icon"><i className="bx bx-edit"></i></Button></Link>
                    </td>
                  </tr>
                )) : <tr><td colSpan="6" className="text-center text-muted">No icon sets</td></tr>}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <strong>Widgets ({widgets?.length || 0})</strong>
            <Link to="/theme-widgets/add"><Button size="sm" variant="outline-primary">Add Widget</Button></Link>
          </Card.Header>
          <Card.Body>
            <Table bordered responsive className="mb-0">
              <thead><tr><th>#</th><th>Name</th><th>Screen</th><th>Category</th><th>Sizes</th><th>Status</th></tr></thead>
              <tbody>
                {widgets && widgets.length ? widgets.map((w, i) => (
                  <tr key={w._id}>
                    <td>{i + 1}</td>
                    <td>{w.widget_name}</td>
                    <td className="text-capitalize">{w.widget_screen}</td>
                    <td>{w.category_name || "-"}</td>
                    <td>{(w.sizes || []).join(", ") || "-"}</td>
                    <td>{w.status ? "Active" : "Inactive"}</td>
                  </tr>
                )) : <tr><td colSpan="6" className="text-center text-muted">No widgets</td></tr>}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </div>
    </Layout>
  );
};

export default ThemeView;
